import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PrePostFilters from "app/popleads/modules/filters/PrePostFilters";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import InputWithIcon from "app/popleads/components/ui-kit/Input";
import { TooltipOverlay } from "app/popleads/components/TooltipOverlay";
import { actions } from "app/popleads/modules/campaigns/redux/campaignsRedux";
import { post } from "app/popleads/networking/RequestService";
import SVG from "react-inlinesvg";

const PrePostActions = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const {
        prePostSearchQuery,
        prePostTypeFilter,
        prePostStatusFilter,
        isPrePostPublishing,
        isPrePostPublished,
        isPrePostPublishStatusLoading,
        prePostAiDisabledReason,
    } = useSelector((state) => state.campaigns);
    const selectedCampaignFromFilter = useSelector((state) => state.settings.selectedCampaignListFromFilter);
    const isPrePostPage = location.pathname.startsWith("/campaigns/pre-post");
    const isPrePostCampaignsPage = location.pathname === "/campaigns/pre-post";
    const selectedCampaignId = Array.isArray(selectedCampaignFromFilter)
        ? selectedCampaignFromFilter[0]?.value
        : selectedCampaignFromFilter?.value;
    const searchParams = new URLSearchParams(location.search);
    const campaignIdFromUrl = searchParams.get("campaignId");
    const courseIdFromUrl = searchParams.get("courseId");
    const canCopyQuestions = Boolean(campaignIdFromUrl || courseIdFromUrl);
    const canImportQuestions = Boolean(campaignIdFromUrl || courseIdFromUrl);
    const hasAnyFilter =
        Boolean(prePostSearchQuery?.trim()) || prePostTypeFilter !== "All" || prePostStatusFilter !== "All";

    const goToQuestionList = () => {
        const redirectUrl = selectedCampaignId
            ? `/campaigns/pre-post/list?campaignId=${selectedCampaignId}`
            : "/campaigns/pre-post/list";

        history.push(redirectUrl);
    };

    // The course's QR code, as on the Attendance screen: delegates scan it to open the course link.
    const getQrCode = () => {
        post({ fn: "getUniqueLinkForSurvey", campaignId: selectedCampaignId }).then((response) => {
            window.open("/api/ws/createQrCode?url=" + response.data.link);
        });
    };

    if (!isPrePostPage) {
        return null;
    }

    if (isPrePostCampaignsPage) {
        return (
            <div className="d-flex justify-content-end align-items-center w-100" style={{ gap: "8px" }}>
                {selectedCampaignId ? (
                    <TooltipOverlay placement="bottom" tooltip="QR Code">
                        <ButtonWithText
                            buttonStyle="selectedOption"
                            variant="neutral"
                            aria-label="QR Code"
                            onClick={getQrCode}
                        >
                            <SVG src="/media/svg/popleads/qr-english.svg" />
                        </ButtonWithText>
                    </TooltipOverlay>
                ) : null}
                {selectedCampaignId ? (
                    <TooltipOverlay placement="bottom" tooltip="Question List">
                        <ButtonWithText
                            buttonStyle="inverted"
                            variant="success"
                            icon="list"
                            aria-label="Question List"
                            onClick={goToQuestionList}
                        />
                    </TooltipOverlay>
                ) : null}
            </div>
        );
    }

    return (
        <div
            className="d-flex flex-column flex-md-row align-items-end align-items-md-center w-100"
            style={{ gap: "8px" }}
        >
            <div className="w-100">
                <InputWithIcon
                    leftIcon="search"
                    rightIcon={prePostSearchQuery?.length > 0 ? "x" : null}
                    minLength={0}
                    debounceTime={350}
                    onChange={(event) => dispatch(actions.setPrePostSearchQuery(event.target.value))}
                    rightIconClick={() => dispatch(actions.setPrePostSearchQuery(""))}
                    leftIconClick={() => dispatch(actions.setPrePostSearchQuery(""))}
                    className="w-100 form-control"
                    placeholder="Search..."
                    value={prePostSearchQuery}
                />
            </div>
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <TooltipOverlay placement="bottom" tooltip={isPrePostPublished ? "Draft" : "Publish"}>
                    <ButtonWithText
                        buttonStyle="inverted"
                        variant={isPrePostPublished ? "danger" : "success"}
                        icon={isPrePostPublished ? "edit-3" : "send"}
                        aria-label={isPrePostPublished ? "Draft" : "Publish"}
                        onClick={() =>
                            dispatch(actions.requestPrePostPublish(isPrePostPublished ? "draft" : "publish"))
                        }
                        disabled={isPrePostPublishing || isPrePostPublishStatusLoading}
                    />
                </TooltipOverlay>
                <TooltipOverlay placement="bottom" tooltip="Add Question">
                    <ButtonWithText
                        buttonStyle="inverted"
                        variant="success"
                        icon="plus"
                        aria-label="Add Question"
                        onClick={() => dispatch(actions.setIsPrePostQuestionModalOpen(true))}
                    />
                </TooltipOverlay>
                {canCopyQuestions ? (
                    <TooltipOverlay placement="bottom" tooltip="Copy Questions">
                        <ButtonWithText
                            buttonStyle="inverted"
                            variant="neutral"
                            icon="copy"
                            aria-label="Copy Questions"
                            onClick={() => dispatch(actions.setIsPrePostCopyModalOpen(true))}
                        />
                    </TooltipOverlay>
                ) : null}
                {canImportQuestions ? (
                    <TooltipOverlay placement="bottom" tooltip="Import Questions">
                        <ButtonWithText
                            buttonStyle="inverted"
                            variant="neutral"
                            icon="upload"
                            aria-label="Import Questions"
                            onClick={() => dispatch(actions.setIsPrePostWordImportModalOpen(true))}
                        />
                    </TooltipOverlay>
                ) : null}
                <TooltipOverlay placement="bottom" tooltip={prePostAiDisabledReason || "Generate Questions with AI"}>
                    {/* A disabled button gets no mouse events, so the wrapper carries the hover for the tooltip. */}
                    <span
                        className="d-inline-block"
                        style={prePostAiDisabledReason ? { cursor: "not-allowed" } : undefined}
                    >
                        <ButtonWithText
                            iconType="material"
                            icon="auto_awesome"
                            materialIconStyle="material-icons"
                            buttonStyle="inverted"
                            variant="primary"
                            aria-label="Generate Questions with AI"
                            disabled={Boolean(prePostAiDisabledReason)}
                            style={prePostAiDisabledReason ? { pointerEvents: "none" } : undefined}
                            onClick={() => dispatch(actions.setIsPrePostAiModalOpen(true))}
                        />
                    </span>
                </TooltipOverlay>
                <PrePostFilters
                    hasAnyFilter={hasAnyFilter}
                    typeFilter={prePostTypeFilter}
                    statusFilter={prePostStatusFilter}
                    setTypeFilter={(value) => dispatch(actions.setPrePostTypeFilter(value))}
                    setStatusFilter={(value) => dispatch(actions.setPrePostStatusFilter(value))}
                    clearFilters={() => {
                        dispatch(actions.setPrePostSearchQuery(""));
                        dispatch(actions.setPrePostTypeFilter("All"));
                        dispatch(actions.setPrePostStatusFilter("All"));
                    }}
                />
            </div>
        </div>
    );
};

export default PrePostActions;
